import { useCallback, useEffect, useMemo, useState } from 'react'
import { AuthProvider, AuthProviderProps, useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router'
import App from '../app/App'
import Loading from '../components/Loading/Loading'
import { isOpenFinView, MAIN_WINDOW_NAME } from '../containers/OpenFin/helpers'
import { parseJwt } from '../helpers/auth'
import { setAuthRefreshFn } from '../helpers/authRefresh'
import { closeAllPopouts } from '../helpers/hooks/useOpenFin'
import { signOut } from '../helpers/signOut'
import {
  fetchAuthToken,
  fetchAuthTokenFailure,
  fetchAuthTokenSuccess,
  redirectToLogin
} from '../store/auth/actions'
import {
  authTokenError,
  authTokenPending,
  getAuthToken,
  getUserName
} from '../store/auth/selectors'
import { addLogItem } from '../store/log/actions'
import { useAppDispatch, useAppSelector } from '../store/types'
import { setSelectedUser } from '../store/users/actions'
import { logError } from '../store/ws/actions'
import OidcProtectedRouteParent from './OidcProtectedRouteParent'
import { SessionContext } from './SessionContext'

import styles from './styles.module.css'

const OidcSecurity = () => {
  // automaticSilentRenew on by default in UserManagerSettings oidc-client-ts,
  // so we just store the new token as it comes in
  const auth = useAuth()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const storedToken = useAppSelector(getAuthToken)
  const tokenPending = useAppSelector(authTokenPending)
  const authError = useAppSelector(authTokenError)
  const userName = useAppSelector(getUserName)

  const [sessionLost, setSessionLost] = useState(false)

  // Session expired (had a token, now unauthenticated). In OpenFin the main grid
  // closes every popout first (closeAllPopouts sets isSigningOut, freezing the
  // snapshot so the layout restores on re-login) then redirects to Keycloak
  const handleSessionLost = useCallback(async () => {
    setSessionLost(true)
    if (isOpenFinView()) {
      if (fin.me.identity.name === MAIN_WINDOW_NAME) {
        await closeAllPopouts()
        void auth.signinRedirect()
      }
      return
    }
    void auth.signinRedirect()
  }, [auth])

  useEffect(() => {
    if (!storedToken && !tokenPending) {
      // doesn't really fetch anything in keycloak, but does turn on the keycloak code
      dispatch(fetchAuthToken())
    }
  }, [storedToken, tokenPending])

  useEffect(() => {
    if (!auth.isAuthenticated) return
    const target = sessionStorage.getItem('preAuthPath')
    if (target) {
      sessionStorage.removeItem('preAuthPath')
      navigate(target, { replace: true })
    }
  }, [auth.isAuthenticated, navigate])

  useEffect(() => {
    if (auth.isAuthenticated) {
      const token = auth.user?.access_token
      if (token && token !== storedToken) {
        const user = parseJwt(token)
        dispatch(fetchAuthTokenSuccess(token, user.name ?? '', user?.sid ?? ''))
      }
    } else if (!auth.isLoading) {
      /*
        Had a session and lost it (overnight expiry) → tear down. A forceReload
        wipes the in-memory storedToken, but auth.user survives in storage as an
        expired user, so it distinguishes a lost session from a true cold boot
        (auth.user absent → sign in normally).
      */
      if (storedToken || auth.user) {
        void handleSessionLost()
      } else {
        auth.signinRedirect()
      }
    } else if (auth.error) {
      dispatch(fetchAuthTokenFailure(auth.error))
    }
  }, [
    auth.isAuthenticated,
    auth.error,
    auth.user,
    auth.user?.access_token,
    storedToken,
    handleSessionLost
  ])

  useEffect(() => {
    setAuthRefreshFn(() =>
      auth.signinSilent().then(() => {
        /* narrow to Promise<void> */
      })
    )
    return () => setAuthRefreshFn(null)
  }, [auth])

  useEffect(() => {
    // react-oidc-context never flips isAuthenticated on a silent-renew failure,
    // so the 400 here is the authoritative "session is over" signal. Retry once
    // (a single failure can be a transient blip); if the retry also rejects, the
    // refresh token is gone — tear down.
    return auth.events.addSilentRenewError(() => {
      auth.signinSilent().catch(() => {
        void handleSessionLost()
      })
    })
  }, [auth, handleSessionLost])

  const logout = useCallback(() => {
    dispatch(addLogItem('logging out user ' + userName))
    // drop any "act on behalf of" selection so it doesn't carry into the next
    // session (resets redux now; the persist epic clears localStorage)
    dispatch(setSelectedUser(0))
    dispatch(redirectToLogin())
    void signOut(auth, (err) => dispatch(logError(err)))
  }, [auth, userName, dispatch])
  const editProfile = useCallback(() => {
    window.open(`${auth.settings.authority}/account`, 'editProfile')
  }, [auth.settings.authority])

  const sessionContextProps = useMemo(() => {
    return { signout: logout, editProfile } as const
  }, [logout, editProfile])

  if (auth.isLoading) {
    return <Loading />
  }

  // Session expired: a popout stops rendering app content (no more worker/data
  // requests) while it waits for the main grid to close it; the main grid is
  // already redirecting to Keycloak.
  if (
    sessionLost &&
    isOpenFinView() &&
    fin.me.identity.name !== MAIN_WINDOW_NAME
  ) {
    return null
  }

  // comments in react-oidc-context suggest not calling signoutRedirect from useEffect
  if (authError) {
    return (
      <div className={styles.centered}>
        <p>
          You have encountered a temporary authentication error. Please log out
          and retry.
        </p>
        <button data-testid="oidc-fail-signout" onClick={logout}>
          Sign out
        </button>
      </div>
    )
  }
  return (
    <SessionContext.Provider value={sessionContextProps}>
      <App SecureRoute={OidcProtectedRouteParent} />
    </SessionContext.Provider>
  )
}
const onSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname)
}
const OidcWrapper = (props: AuthProviderProps) => {
  return (
    <AuthProvider onSigninCallback={onSigninCallback} {...props}>
      <OidcSecurity />
    </AuthProvider>
  )
}

export default OidcWrapper
